import React, { useCallback, useEffect, useState } from 'react';
import { transactionTemplatesService } from '../../services/transactionTemplates.service';
import './TransactionChecklistForms.css';

export const STATUS_LABELS = { PENDING: 'Pending', UPLOADED: 'Added', SIGNED: 'Signed', NOT_APPLICABLE: 'N/A' };
// Same pill colours as the Forms list (Draft/awaiting = amber, signed = green).
export const STATUS_STYLES = {
  PENDING: { background: '#fff4e6', color: '#b06a00' },
  UPLOADED: { background: '#e8f5e9', color: '#2e7d32' },
  SIGNED: { background: '#e8f5e9', color: '#2e7d32' },
  NOT_APPLICABLE: { background: '#F5F5F7', color: '#666' },
};
// Matches TransactionDocuments' DYN_ROW_MAX so checklist rows line up with form rows.
export const ROW_HEIGHT = 84;

/**
 * Folder heading shared by the checklist and the Forms list folder groups.
 * Clicking it expands/collapses the folder's rows.
 */
export const FolderHeading = ({ name, count, className, expanded, onToggle }) => (
  <button
    type="button"
    className={`txn-folder-heading ${className || ''}`.trim()}
    onClick={onToggle}
    aria-expanded={expanded}
  >
    <span className={`txn-folder-heading-chevron${expanded ? ' is-open' : ''}`} aria-hidden="true">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
    </span>
    <span className="txn-folder-heading-icon" aria-hidden="true">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
    </span>
    <span>{name}</span>
    {count != null && <span className="txn-folder-heading-count">{count}</span>}
  </button>
);

/** Collapsed-folder bookkeeping; every folder starts expanded. */
export const useCollapsedFolders = () => {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggle = useCallback((slug) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  }, []);
  return { isExpanded: (slug) => !collapsed.has(slug), toggle };
};

const sameCode = (a, b) => (a || '').trim().toUpperCase() === (b || '').trim().toUpperCase();

/** Template form code of a Forms-tab row: CA Lone Wolf acronym, CO forms-service formId. */
export const formCodeOf = (doc) => (doc._loneWolf ? doc._acronym : doc._formService ? doc.formId : '') || '';
const AUTO_ADDED_SOURCES = ['LONEWOLF', 'FORMS_SERVICE'];
// Transactions already auto-retried in this browser session — remounting the Documents tab
// must not start another run (overlapping runs could add the same form twice).
const autoRetried = new Set();

/**
 * Template forms are added to LoneWolf automatically — on create, by the backend, each
 * into its template folder. No selection or confirmation. This only shows when some form
 * is still waiting (e.g. LoneWolf wasn't connected at create): it retries the auto-add
 * once on open, shows progress, and offers Retry if a form still couldn't be added.
 */
const TransactionChecklistForms = ({ transaction, attachedForms, onFormAdded, showStatus = true }) => {
  const [pendingItems, setPendingItems] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState('');

  const loadPending = useCallback(async () => {
    if (!transaction?.id) return [];
    try {
      const list = await transactionTemplatesService.getChecklistItems(transaction.id);
      const pending = list.filter((i) => i.type === 'FORM' && AUTO_ADDED_SOURCES.includes(i.formSource) && i.status === 'PENDING');
      setPendingItems(pending);
      return pending;
    } catch (e) {
      console.error('[TransactionChecklistForms] Failed to load checklist items', e);
      return [];
    }
  }, [transaction?.id]);

  const addTemplateForms = useCallback(async () => {
    setError('');
    setIsAdding(true);
    try {
      const data = await transactionTemplatesService.attachTemplateForms(transaction.id);
      const failed = Array.isArray(data?.failed) ? data.failed : [];
      if (failed.length > 0) setError(`Couldn't add: ${failed.join(', ')}.`);
    } catch (e) {
      console.error('[TransactionChecklistForms] Template forms auto-add failed', e);
      setError(e.response?.status === 409
        ? 'Connect your LoneWolf account to add the template forms.'
        : "Couldn't add the template forms.");
    } finally {
      setIsAdding(false);
      await loadPending();
      onFormAdded();
    }
  }, [transaction?.id, loadPending, onFormAdded]);

  // Retry the auto-add once per transaction when forms are still waiting.
  useEffect(() => {
    let cancelled = false;
    loadPending().then((pending) => {
      if (cancelled || pending.length === 0 || autoRetried.has(transaction?.id)) return;
      autoRetried.add(transaction?.id);
      addTemplateForms();
    });
    return () => { cancelled = true; };
  }, [transaction?.id, loadPending, addTemplateForms]);

  const waiting = pendingItems.filter(
    (item) => !attachedForms.some((doc) => sameCode(formCodeOf(doc), item.formCode)),
  );
  if (!showStatus || (waiting.length === 0 && !isAdding)) return null;

  return (
    <div className="txn-checklist-status" role="status">
      {isAdding ? (
        <span>Adding template forms… ({waiting.length})</span>
      ) : (
        <>
          <span className="txn-checklist-error">{error || `${waiting.length} template forms are still to be added.`}</span>
          <button type="button" className="txn-checklist-retry" onClick={addTemplateForms}>Retry</button>
        </>
      )}
    </div>
  );
};

export default TransactionChecklistForms;
